/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import { FancyList, FancyListItem } from 'src/_components/FancyList';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function Authentication() {
  const canViewPasswordGrant = false;

  return (
    <>
      <Meta
        title="Working with Authentication"
        description="Explore OAuth 2.0 on the Vimeo API, learn how authentication works, and get step-by-step instructions for each of the supported workflows."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Current>Working with Authentication</Path.Current>
          </Path>
        }
        nav={{ page: 'authentication' }}
      >
        <Header>Working with Authentication</Header>

        <Paragraph size="2">
          <em>Authentication</em> is the process by which your application gains the ability to
          access Vimeo data and resources through the API. There are several ways that you can go
          about this, depending on the type of data that your app needs to access and the particular
          conditions under which you're accessing it. You'll learn about them all in this tutorial.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> This information is current for our latest API release, version
          3.4. For complete details on 3.4, see our{' '}
          <Link variant="minimal" href="/api/changelog">
            Changelog
          </Link>
          .
        </StyledNotice>

        <Header size="2" id="before-you-begin">
          Before you begin
        </Header>

        <Paragraph size="2">To use this guide, you need:</Paragraph>

        <ul>
          <li>
            <strong>A registered API app.</strong> This is what's interacting with the API and
            requesting authentication. Haven't registered yet? See{' '}
            <Link variant="minimal" href="/api/guides/start">
              Getting Started: The Basics
            </Link>
            .
          </li>
        </ul>

        <StyledNotice>
          <strong>NOTE:</strong> You might also consider using our official libraries for PHP,
          Python, Node.js, and a dozen other programming languages. The libraries help to simplify
          complicated coding by handling all the HTTP requests on your behalf. For more information,
          see our{' '}
          <Link variant="minimal" href="/api/libraries">
            Libraries and SDKs
          </Link>{' '}
          page.
        </StyledNotice>

        <Header size="2" id="understanding-the-auth-process">
          Understanding the authentication process
        </Header>

        <Paragraph size="2">
          The Vimeo API uses the open{' '}
          <Link variant="minimal" href="https://oauth.net/">
            OAuth 2.0
          </Link>{' '}
          standard for all your app-authentication needs. Under the OAuth 2.0 framework, a third
          party — that's you — asks a resource owner — that's your app's end user — for permission
          to access their Vimeo data. If the resource owner grants this permission, we issue you an{' '}
          <em>access token</em>. This is a special string of characters that works like a password.
          You present the access token with your API requests whenever you're asking for protected
          information.
        </Paragraph>

        <Paragraph size="2">
          What we've just described is an <em>authenticated</em> access token, because it's tied to
          a particular Vimeo member's private data. But not all Vimeo data is private. Some of it is
          publicly available, so that a third party — you again — doesn't need anyone's permission
          to access it. You still need to obtain an access token, though, because we need to know
          who's asking for this data. An <em>unauthenticated</em> access token serves this purpose.
          It isn't tied to a particular Vimeo member, but it does get you into the lobby, so to
          speak, for access to our public data. You can access public data with an authenticated
          access token, too; it's not exclusively for private data. It's also the type of token that
          you need to perform certain actions like uploading videos. But an unauthenticated access
          token is always for public data only.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> If your app accesses the private data of multiple Vimeo members,
          you need a separate authenticated access token specific to each account.
        </StyledNotice>

        <Paragraph size="2">
          An access token has one or more scopes, or general domains. The scopes determine the types
          of access that token does and doesn't permit. During the authentication process, you can
          specify exactly which scopes your app needs based on the list in{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>
          . If you try to make an API request with an access token that doesn't have the right
          scope, the API will deny the request.
        </Paragraph>

        <GuideTable
          tableNumber={1}
          description="Access scopes"
          headers={['Scope', 'Enables your app to']}
          rows={[
            [<code>public*</code>, 'Access public member data.'],
            [<code>private†</code>, 'Access private member data.'],
            [<code>purchased</code>, "Access a member's Vimeo On Demand purchase history."],
            [
              <code>create</code>,
              <span>
                Create new Vimeo resources like showcases, groups, channels, and portfolios. To
                create new videos, you need the <code>upload</code> scope.
              </span>,
            ],
            [<code>edit</code>, 'Edit existing Vimeo resources, including videos.'],
            [<code>delete</code>, 'Delete existing Vimeo resources, including videos.'],
            [
              <code>interact</code>,
              'Interact with Vimeo resources, such as liking a video or following a member.',
            ],
            [
              <code>stats</code>,
              "Be able to access video stats. Please note that this scope doesn't automatically grant your app the necessary capabilities for specific stats.",
            ],
            [<code>upload</code>, 'Upload videos.'],
            [<code>promo_codes</code>, 'Add, remove, and review Vimeo On Demand promotions.'],
            [
              <code>video_files</code>,
              'Access video files belonging to members with Vimeo Standard membership or higher.',
            ],
          ]}
        />

        <Paragraph size="4">
          * An authenticated access token with the <code>public</code> scope is identical to an
          unauthenticated access token, except that you can use the <code>/me</code> endpoint to
          refer to the currently logged-in user. Accessing <code>/me</code> with an unauthenticated
          access token generates an error.
        </Paragraph>

        <Paragraph size="4">
          † Required for any scope other than <code>public</code>.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> Expiration times for access tokens vary, depending on the type of
          the token and the way that the token is granted. Some access tokens have a limited
          lifespan. Others continue to work indefinitely, as long as you use them on a regular
          basis. To keep things running smoothly on our end, we automatically delete any tokens that
          appear to be inactive. For more information, see the descriptions of the individual
          authentication workflows below.
        </StyledNotice>

        <Header size="2" id="understanding-auth-workflows">
          Understanding authentication workflows
        </Header>

        <Paragraph size="2">
          OAuth 2.0 supports a number of different methods or <em>workflows</em> for authentication
          based on the <em>grant type</em>, or the way in which authentication is granted.
        </Paragraph>

        <Paragraph size="2">
          For the purposes of the Vimeo API, there are {canViewPasswordGrant ? 'five' : 'four'}{' '}
          grant types: client credentials, authorization code, implicit, and device code. You want
          to choose the one that works the best for your particular circumstances.
        </Paragraph>

        {canViewPasswordGrant && (
          <StyledNotice>
            <strong>NOTE:</strong> A fifth grant type, the password type, is also available for your
            Vimeo account.
          </StyledNotice>
        )}

        <Paragraph size="2">
          We'll have more to say about each grant type when we go through the workflows step by
          step. In the meantime,{' '}
          <Link variant="minimal" href="#table-2">
            Table 2
          </Link>{' '}
          provides a quick summary.
        </Paragraph>

        <GuideTable
          tableNumber={2}
          description="Summary of grant types"
          headers={['Grant type', 'Enables access to', 'Use when']}
          rows={[
            ['Client credentials', 'Public data', "You don't need access to private data."],
            ['Authorization code', 'Public data, private data', 'You need long-term access.'],
            [
              'Implicit',
              'Public data, private data',
              "You can't properly secure your app's client secret from your end users.",
            ],
            [
              'Device code',
              'Public data, private data',
              'Your app runs on a browserless or limited-input device.',
            ],
            canViewPasswordGrant && [
              'Password',
              'Public data, private data',
              "Your app runs on a device that can't render a web page.",
            ],
          ]}
        />

        <StyledNotice>
          <strong>NOTE:</strong> We also support the basic authentication workflow, which doesn't
          have anything to do with OAuth or grant types. We don't recommend this kind of
          authentication in most cases, because using it can lead to security problems. With that
          proviso, see{' '}
          <Link variant="minimal" href="#working-with-basic-authentication">
            Working with basic authentication
          </Link>{' '}
          below for more information.
        </StyledNotice>

        <Header size="2" id="obtaining-an-access-token">
          Obtaining an access token
        </Header>

        <Paragraph size="2">
          So to summarize what we've covered so far: if you want to make API requests, you need to
          have an access token. And if you want to get an access token, you need to follow an
          authentication workflow. But which of the workflows is best for you? And what are the
          steps? And how do they work?
        </Paragraph>

        <Paragraph size="2">
          The following sections explain the grant types in detail and take you through the
          workflows from start to finish.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> Are you in the market for a personal access token? Do you want to
          generate it here on the developer site instead of through the API? See{' '}
          <Link variant="minimal" href="/api/guides/start#generate-access-token">
            Getting Started: The Basics
          </Link>{' '}
          for step-by-step instructions.
        </StyledNotice>

        <Header size="3" id="using-the-client-credentials-grant">
          Using the client credentials grant
        </Header>

        <Paragraph size="2">
          The <em>client credentials</em> grant provides access to public data only. You don't need
          permission from your end user for that, so the entire authentication workflow happens
          without their participation.
        </Paragraph>

        <Paragraph size="2">Here are the general steps:</Paragraph>

        <FancyList anchorPrefix="using-the-client-credentials-grant">
          <FancyListItem header="Make a POST request.">
            <Paragraph size="2">
              Make a POST request to <code>/oauth/authorize/client</code>:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'post', resource: '/oauth/authorize/client' }} />

            <Paragraph size="2">
              <Link variant="minimal" href="#table-3">
                Table 3
              </Link>{' '}
              shows the headers to send. For the{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              header, you need your app's client identifier and client secret, both of which appear
              on the app's information page. To access this page, go to{' '}
              <Link variant="minimal" href="/apps">
                My Apps
              </Link>
              .
            </Paragraph>

            <GuideTable
              tableNumber={3}
              description={
                <span>
                  Headers for POST to <code>/oauth/authorize/client</code> in the client credentials
                  grant
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Authorization</b>
                  </code>,
                  <span>
                    <code>basic base64_encode(x:y),</code> where{' '}
                    <code>
                      <b>x</b>
                    </code>{' '}
                    is the client identifier and{' '}
                    <code>
                      <b>y</b>
                    </code>{' '}
                    is the client secret
                  </span>,
                ],
                [
                  <code>
                    <b>Content-Type</b>
                  </code>,
                  <code>application/json</code>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <StyledNotice>
              <strong>NOTE:</strong> Don't omit the{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              header in this request (and all subsequent requests in this guide), and always be sure
              to set it to the given value. This ensures that the API handles your response
              according to version 3.4. For more information, see
              <Link variant="minimal" href="/api/common-formats#using-the-accept-header">
                {' '}
                Using Common Formats and Parameters
              </Link>
              .
            </StyledNotice>

            <Paragraph size="2">
              In the body of the request, set the{' '}
              <code>
                <b>grant_type</b>
              </code>{' '}
              field to <code>client_credentials</code>; this is required.
            </Paragraph>

            <CodeBlock language="json">
              {{
                grant_type: 'client_credentials',
                scope: 'public',
              }}
            </CodeBlock>

            <StyledNotice>
              <strong>NOTE:</strong> You can't use the access token from the client credentials
              grant to get anything other than public data, even if you specify additional scopes.
            </StyledNotice>

            <Paragraph size="2">
              <Link variant="minimal" href="#table-4">
                Table 4
              </Link>{' '}
              presents descriptions of common errors for this API request.
            </Paragraph>

            <GuideTable
              tableNumber={4}
              description={
                <span>
                  Common errors for POST to <code>/oauth/authorize/client</code> in the client
                  credentials grant
                </span>
              }
              headers={['HTTP code', 'Error code', 'Description']}
              rows={[
                ['400', '2204', 'You have an invalid value for the grant_type field.'],
                ['401', '8001', "We don't recognize your API app."],
              ]}
            />
          </FancyListItem>
          <FancyListItem header="Get the access token.">
            <Paragraph size="2">
              The response from the API includes the access token string among some other technical
              details (see{' '}
              <Link variant="minimal" href="#table-5">
                Table 5
              </Link>
              ).
            </Paragraph>

            <CodeBlock language="json">
              {{
                access_token: '{token}',
                token_type: 'bearer',
                scope: '{scope_list}',
              }}
            </CodeBlock>

            <GuideTable
              tableNumber={5}
              description="OAuth response fields in the client credentials grant"
              headers={['Field', 'Description']}
              rows={[
                [
                  <code>
                    <b>access_token</b>
                  </code>,
                  'The access token string.',
                ],
                [
                  <code>
                    <b>token_type</b>
                  </code>,
                  <span>
                    The type of the access token. For the Vimeo API, the value of this field is
                    always <code>bearer</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>scope</b>
                  </code>,
                  <span>
                    The space-separated list of scopes associated with the access token. The default
                    value is <code>public</code>. Any other scopes that you may have specified in{' '}
                    <Link variant="minimal" href="#using-the-client-credentials-grant-step-1">
                      Step 1
                    </Link>{' '}
                    are ignored.
                  </span>,
                ],
              ]}
            />
          </FancyListItem>
        </FancyList>

        <Header size="3" id="using-the-auth-code-grant">
          Using the authorization code grant
        </Header>

        <Paragraph size="2">
          With the <em>authorization code</em> grant, the end user explicitly consents for your app
          to access their private data, including exactly which scopes to permit or deny. The front
          end and logic of the consent mechanism are all on our side; you send your end user to us
          through a browser, and we take care of it. You don't even need to log them in. We send
          them back to you with an authorization code, which you present to the API in exchange for
          an access token. Unlike in the{' '}
          <Link variant="minimal" href="#using-the-implicit-grant">
            implicit grant
          </Link>
          , this access token resides on the server, and it remains active as long as we perceive
          that you're using it.
        </Paragraph>

        <Paragraph size="2">Here are the general steps:</Paragraph>

        <FancyList anchorPrefix="using-the-auth-code-grant">
          <FancyListItem header="Send your end user to the authorization URL.">
            <Paragraph size="2">Direct your end user to the following address:</Paragraph>

            <CodeBlock
              endpoint={{
                resource: '/oauth/authorize',
                params:
                  '?response_type=code&client_id={client_id}&redirect_uri={redirect_uri}&state={state}&scope={scope_list}',
              }}
            />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-6">
                Table 6
              </Link>{' '}
              for details about the query parameters.
            </Paragraph>

            <GuideTable
              tableNumber={6}
              description={
                <span>
                  Query parameters for <code>/oauth/authorize</code> in the authorization code grant
                </span>
              }
              headers={['Query parameter', 'Description']}
              rows={[
                [
                  <code>
                    <b>response_type</b>
                  </code>,
                  <span>
                    The OAuth response type. Use <code>code</code> for the authorization code grant.
                  </span>,
                ],
                [
                  <code>
                    <b>client_id</b>
                  </code>,
                  <span>
                    The client identifier of your app. To get this value, access your app's
                    information page from{' '}
                    <Link variant="minimal" href="/apps">
                      My Apps
                    </Link>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>redirect_uri</b>
                  </code>,
                  <span>
                    A redirect URI associated with your app. To add or review redirect URIs, access
                    your app's information page from{' '}
                    <Link variant="minimal" href="/apps">
                      My Apps
                    </Link>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>state</b>
                  </code>,
                  'An arbitrary value. We return this value to you later as a security measure.',
                ],
                [
                  <code>
                    <b>scope</b>
                  </code>,
                  <span>
                    The space-separated list of scopes that you want to be able to access. This
                    field is optional, and its default value is <code>public private</code>.
                  </span>,
                ],
              ]}
            />

            <Paragraph size="2">
              It doesn't matter how you get your end user to the authorization URL. One of the most
              common ways is to include the link on a web page. Just make sure that you use the
              correct parameter values for{' '}
              <code>
                <b>response_type</b>
              </code>
              ,{' '}
              <code>
                <b>redirect_uri</b>
              </code>
              ,{' '}
              <code>
                <b>scope</b>
              </code>
              , and so on. If you don't, the request fails, and the standard Vimeo 404 page loads.
            </Paragraph>
          </FancyListItem>
          <FancyListItem header="Receive access permission from your end user.">
            <Paragraph size="2">
              As soon as your end user arrives, we enable them to create a new Vimeo account if they
              don't already have one or log into their existing Vimeo account (assuming that they
              aren't already logged in), and then we ask them to accept or deny your app's request
              to access their private information. They can also choose which access scopes to
              permit if you asked for any scope other than <code>public</code>.
            </Paragraph>
          </FancyListItem>
          <FancyListItem header="Get the authorization code.">
            <Paragraph size="2">
              Once they accept, we send them to the redirect URI, along with the code and state
              query parameters:
            </Paragraph>

            <CodeBlock
              endpoint={{
                httpPrefix: false,
                resource: '{redirect_uri}',
                params: '?code={code}&state={state}',
              }}
            />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-7">
                Table 7
              </Link>{' '}
              for more information.
            </Paragraph>

            <GuideTable
              tableNumber={7}
              description="Query parameters appended to the redirect URI in the authorization code grant"
              headers={['Query parameter', 'Description']}
              rows={[
                [
                  <code>
                    <b>code</b>
                  </code>,
                  'The authorization code to exchange for an access token.',
                ],
                [
                  <code>
                    <b>state</b>
                  </code>,
                  <span>
                    The value that you specified for the{' '}
                    <code>
                      <b>state</b>
                    </code>{' '}
                    query parameter in the authorization URL. If this value doesn't match the
                    original, don't attempt to exchange the code for a token.
                  </span>,
                ],
              ]}
            />

            <StyledNotice>
              <strong>NOTE:</strong> We retain any custom query parameters that you include with
              your URI.
            </StyledNotice>
          </FancyListItem>
          <FancyListItem header="Send us the authorization code.">
            <Paragraph size="2">
              You now have the authorization code that you can exchange for an access token, so make
              a POST request to <code>/oauth/access_token</code>:
            </Paragraph>

            <CodeBlock
              endpoint={{
                method: 'post',
                resource: '/oauth/access_token',
              }}
            />

            <Paragraph size="2">
              The headers to send appear in{' '}
              <Link variant="minimal" href="#table-8">
                Table 8
              </Link>
              . You can get your app's client identifier and client secret, which you need for the{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              header, from the app's information page at{' '}
              <Link variant="minimal" href="/apps">
                My Apps
              </Link>
              .
            </Paragraph>

            <GuideTable
              tableNumber={8}
              description={
                <span>
                  Headers for POST to <code>/oauth/access_token</code> in the authorization code
                  grant
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Authorization</b>
                  </code>,
                  <span>
                    <code>basic base64_encode(x:y),</code> where{' '}
                    <code>
                      <b>x</b>
                    </code>{' '}
                    is the client identifier and{' '}
                    <code>
                      <b>y</b>
                    </code>{' '}
                    is the client secret
                  </span>,
                ],
                [
                  <code>
                    <b>Content-Type</b>
                  </code>,
                  <code>application/json</code>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <Paragraph size="2">
              In the request body, send the{' '}
              <code>
                <b>grant_type</b>
              </code>{' '}
              field with the value <code>authorization_code</code>. You must also set{' '}
              <code>
                <b>code</b>
              </code>{' '}
              to the authorization code string that you just received and{' '}
              <code>
                <b>redirect_uri</b>
              </code>{' '}
              to the redirect URI that you specified previously — don't use a different redirect
              URI.
            </Paragraph>

            <CodeBlock language="json">
              {{
                grant_type: 'authorization_code',
                code: '{code}',
                redirect_uri: '{redirect_uri}',
              }}
            </CodeBlock>

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-9">
                Table 9
              </Link>{' '}
              for descriptions of common errors.
            </Paragraph>

            <GuideTable
              tableNumber={9}
              description={
                <span>
                  Common errors for POST to <code>/oauth/access_token</code> in the authorization
                  code grant
                </span>
              }
              headers={['HTTP code', 'Error code', 'Description']}
              rows={[
                [
                  '400',
                  <code>invalid_token</code>,
                  "Your code isn't valid, or the callback and the redirect URI don't match.",
                ],
                [
                  '400',
                  <code>unsupported_grant_type</code>,
                  <span>
                    The value of the{' '}
                    <code>
                      <b>grant_type</b>
                    </code>{' '}
                    field isn't <code>authorization_code</code>.
                  </span>,
                ],
              ]}
            />
          </FancyListItem>
          <FancyListItem header="Get the access token.">
            <Paragraph size="2">
              The API responds with the access token and details about the authenticated user (see{' '}
              <Link variant="minimal" href="#table-10">
                Table 10
              </Link>
              ).
            </Paragraph>

            <CodeBlock language="json">
              {{
                access_token: '{token}',
                token_type: 'bearer',
                scope: '{scope_list}',
                user: '{user_representation}',
              }}
            </CodeBlock>

            <GuideTable
              tableNumber={10}
              description="OAuth response fields in the authorization code grant"
              headers={['Field', 'Description']}
              rows={[
                [
                  <code>
                    <b>access_token</b>
                  </code>,
                  'The access token string.',
                ],
                [
                  <code>
                    <b>token_type</b>
                  </code>,
                  <span>
                    The type of the access token. For the Vimeo API, the value of this field is
                    always{' '}
                    <code>
                      <b>bearer</b>
                    </code>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>scope</b>
                  </code>,
                  'The space-separated list of scopes that your app has permission to access.',
                ],
                [
                  <code>
                    <b>user</b>
                  </code>,
                  <span>
                    The complete representation of the authenticated end user. For more information,
                    see the{' '}
                    <Link variant="minimal" href="/api/reference/users#get_user">
                      API Reference
                    </Link>
                    .
                  </span>,
                ],
              ]}
            />
          </FancyListItem>
        </FancyList>

        <Header size="3" id="using-the-implicit-grant">
          Using the implicit grant
        </Header>

        <Paragraph size="2">
          With the <em>implicit grant</em>, your app gains access to private data, but the workflow
          is abbreviated. You don't receive an authorization code to exchange for an access token
          like you do in the{' '}
          <Link variant="minimal" href="#using-the-auth-code-grant">
            authorization code grant
          </Link>
          . The API just gives you the access token, and you get it through the browser, as part of
          a URL. The access token has a one-hour lifespan.
        </Paragraph>

        <Paragraph size="2">
          To obtain an access token with the implicit grant, first make sure that your app is set up
          for it. Go to{' '}
          <Link variant="minimal" href="/apps">
            My Apps
          </Link>
          , open your app's information page, look under the settings for OAuth redirect
          authentication, and, if necessary, click the control for enabling implicit authentication.
        </Paragraph>

        <Paragraph size="2">
          Once you're sure that you've turned on this setting, here are the general steps:
        </Paragraph>

        <FancyList anchorPrefix="using-the-implicit-grant">
          <FancyListItem header="Send your end user to the authorization URL.">
            <Paragraph size="2">Send your end user here:</Paragraph>

            <CodeBlock
              endpoint={{
                resource: '/oauth/authorize',
                params:
                  '?response_type=token&client_id={client_id}&redirect_uri={redirect_uri}&state={state}&scope={scope_list}',
              }}
            />

            <Paragraph size="2">
              For descriptions of the query parameters, see{' '}
              <Link variant="minimal" href="#table-11">
                Table 11
              </Link>
              .
            </Paragraph>

            <GuideTable
              tableNumber={11}
              description={
                <span>
                  Query parameters for <code>/oauth/authorize</code> in the implicit grant
                </span>
              }
              headers={['Query parameter', 'Description']}
              rows={[
                [
                  <code>
                    <b>response_type</b>
                  </code>,
                  <span>
                    The OAuth response type. Use <code>token</code> for the implicit grant.
                  </span>,
                ],
                [
                  <code>
                    <b>client_id</b>
                  </code>,
                  <span>
                    The client identifier of your app. To get this value, access your app's
                    information page from{' '}
                    <Link variant="minimal" href="/apps">
                      My Apps
                    </Link>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>redirect_uri</b>
                  </code>,
                  <span>
                    A redirect URI associated with your app. To add or review redirect URIs, access
                    your app's information page from{' '}
                    <Link variant="minimal" href="/apps">
                      My Apps
                    </Link>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>state</b>
                  </code>,
                  'An arbitrary value. The API returns this value to you later as a security measure.',
                ],
                [
                  <code>
                    <b>scope</b>
                  </code>,
                  <span>
                    The space-separated list of scopes that you want to be able to access. This
                    field is optional, and its default value is <code>public private</code>.
                  </span>,
                ],
              ]}
            />

            <Paragraph size="2">
              If the values of these parameters are invalid, the request fails, and your end user
              goes to the Vimeo 404 page.
            </Paragraph>
          </FancyListItem>
          <FancyListItem header="Receive access permission from your end user.">
            <Paragraph size="2">
              What happens next is similar to{' '}
              <Link variant="minimal" href="#using-the-auth-code-grant-step-2">
                Step 2 of the authorization code grant
              </Link>
              . Your end user creates a new account or logs in, accepts or denies your app's request
              for access, and chooses which scopes (other than <code>public</code>) to allow.
            </Paragraph>
          </FancyListItem>
          <FancyListItem header="Get the access token.">
            <Paragraph size="2">
              We send the end user to your redirect URI, but we append the access token to it, along
              with other information, in a URL fragment (the part after the hash sign):
            </Paragraph>

            <CodeBlock
              endpoint={{
                httpPrefix: false,
                resource: '{redirect_uri}',
                params:
                  '#access_token={access_token}&token_type=bearer&scope={scope_list}&state={state}&expires_in={expires_in}',
              }}
            />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-12">
                Table 12
              </Link>{' '}
              for descriptions of these fields.
            </Paragraph>

            <GuideTable
              tableNumber={12}
              description="Fields appended to the redirect URI in the implicit grant"
              headers={['Field', 'Data type', 'Description']}
              rows={[
                [
                  <code>
                    <b>access_token</b>
                  </code>,
                  'String',
                  'The access token string.',
                ],
                [
                  <code>
                    <b>token_type</b>
                  </code>,
                  'String',
                  <span>
                    The type of the access token, which for the Vimeo API is always{' '}
                    <code>
                      <b>bearer</b>
                    </code>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>scope</b>
                  </code>,
                  'String',
                  'The plus-separated list of scopes that your app has permission to access.',
                ],
                [
                  <code>
                    <b>state</b>
                  </code>,
                  'String',
                  <span>
                    The value that you specified for the{' '}
                    <code>
                      <b>state</b>
                    </code>{' '}
                    query parameter in the authorization URL. Be sure that this value matches the
                    original. If it doesn't, a third party might be attempting to hijack the
                    authorization process.
                  </span>,
                ],
                [
                  <code>
                    <b>expires_in</b>
                  </code>,
                  'Integer',
                  'The lifespan of the access token, in seconds. After this amount of time passes, the token automatically expires.',
                ],
              ]}
            />

            <Paragraph size="2">
              If your redirect URI includes query parameters, we return them, but we strip out any
              custom URL fragment, again as marked by the hash sign. So if you give us the following
              as the redirect URI:
            </Paragraph>

            <CodeBlock>
              https://www.example.com?queryparam=queryvalue#customparam=customvalue
            </CodeBlock>

            <Paragraph size="2">
              We remove everything after the hash sign and substitute the fields from{' '}
              <Link variant="minimal" href="#table-12">
                Table 12
              </Link>{' '}
              above.
            </Paragraph>

            <CodeBlock>{`https://www.example.com?queryparam=queryvalue#access_token={access_token}&token_type=bearer&scope={scope_list}&state={state}&expires_in={expires_in}`}</CodeBlock>
          </FancyListItem>
        </FancyList>

        <Header size="3" id="using-the-device-code-grant">
          Using the device code grant
        </Header>

        <Paragraph size="2">
          Not every internet-connected device includes a browser, and many of these devices are{' '}
          <em>input-constrained</em>, or difficult to use to enter information. The{' '}
          <em>device code grant</em> is for cases like these. In this workflow, the browserless
          device displays a special URL and code. Your end user switches to a computer or tablet,
          opens a browser, goes to the URL, and enters the code. Back in the browserless device,
          your app intercepts that the code has been entered and receives an access token from the
          API.
        </Paragraph>

        <Paragraph size="2">Here are the general steps:</Paragraph>

        <FancyList anchorPrefix="using-the-device-code-grant">
          <FancyListItem header="Make a POST request.">
            <Paragraph size="2">
              Make a POST request to <code>/oauth/device</code>:
            </Paragraph>

            <CodeBlock
              endpoint={{
                method: 'post',
                resource: '/oauth/device',
              }}
            />

            <Paragraph size="2">
              Along with this request, be sure to send some headers (see{' '}
              <Link variant="minimal" href="#table-13">
                Table 13
              </Link>
              ). The client identifier and client secret that you need to complete the value of{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              are on your app's information page, which you can open from{' '}
              <Link variant="minimal" href="/apps">
                My Apps
              </Link>
              .
            </Paragraph>

            <GuideTable
              tableNumber={13}
              description={
                <span>
                  Headers for POST to <code>/oauth/device</code> in the device code grant
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Authorization</b>
                  </code>,
                  <span>
                    <code>basic base64_encode(x:y),</code> where{' '}
                    <code>
                      <b>x</b>
                    </code>{' '}
                    is the client identifier and{' '}
                    <code>
                      <b>y</b>
                    </code>{' '}
                    is the client secret
                  </span>,
                ],
                [
                  <code>
                    <b>Content-Type</b>
                  </code>,
                  <code>application/json</code>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <Paragraph size="2">
              Use the body of the request to send the{' '}
              <code>
                <b>grant_type</b>
              </code>{' '}
              field. Set this value to <code>device_grant</code>. If you want to request specific
              scopes, include a space-separated list in the{' '}
              <code>
                <b>scope</b>
              </code>{' '}
              field. If you don't make a specific request, the default{' '}
              <code>
                <b>scope</b>
              </code>{' '}
              value is <code>public private</code>.
            </Paragraph>

            <CodeBlock language="json">
              {{
                grant_type: 'device_grant',
                scope: '{scope_list}',
              }}
            </CodeBlock>

            <Paragraph size="2">
              Did something go wrong? See{' '}
              <Link variant="minimal" href="#table-14">
                Table 14
              </Link>{' '}
              for common errors.
            </Paragraph>

            <GuideTable
              tableNumber={14}
              description={
                <span>
                  Common errors for POST to <code>/oauth/device</code> in the device code grant
                </span>
              }
              headers={['HTTP code', 'Error code', 'Description']}
              rows={[
                [
                  '400',
                  '2204',
                  <span>
                    You have an invalid value for the{' '}
                    <code>
                      <b>grant_type</b>
                    </code>{' '}
                    field.
                  </span>,
                ],
                ['401', '8001', "We don't recognize your API app."],
              ]}
            />
          </FancyListItem>
          <FancyListItem header="Get the user code, authorization endpoint, and activation link.">
            <Paragraph size="2">
              The API comes back with a number of fields, which we summarize in{' '}
              <Link variant="minimal" href="#table-15">
                Table 15
              </Link>
              , but pay particular attention to{' '}
              <code>
                <b>user_code</b>
              </code>
              ,{' '}
              <code>
                <b>authorize_link</b>
              </code>
              , and{' '}
              <code>
                <b>activate_link</b>
              </code>
              . You'll need these values to proceed.
            </Paragraph>

            <GuideTable
              tableNumber={15}
              description="First set of OAuth response fields in the device code grant"
              headers={['Field', 'Description']}
              rows={[
                [
                  <code>
                    <b>device_code</b>
                  </code>,
                  "A unique identifier for the end user's device.",
                ],
                [
                  <code>
                    <b>user_code</b>
                  </code>,
                  'The code that the end user must enter.',
                ],
                [
                  <code>
                    <b>authorize_link</b>
                  </code>,
                  <span>
                    The authorization endpoint:{' '}
                    <code>https://api.vimeo.com/oauth/device/authorize</code>
                  </span>,
                ],
                [
                  <code>
                    <b>activate_link</b>
                  </code>,
                  <span>
                    The URL that the end user must visit to enter the value of{' '}
                    <code>
                      <b>user_code</b>
                    </code>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>expires_in</b>
                  </code>,
                  'The lifespan of the user code, in seconds. After 3600 seconds, the user code expires.',
                ],
                [
                  <code>
                    <b>interval</b>
                  </code>,
                  'The number of seconds that you should wait between checks when polling the authorization endpoint.',
                ],
              ]}
            />
          </FancyListItem>
          <FancyListItem header="Send your end user to the activation link to enter the user code.">
            <Paragraph size="2">
              Instruct the end user to find a browser, visit the URL in{' '}
              <code>
                <b>activate_link</b>
              </code>
              , and enter the code in{' '}
              <code>
                <b>user_code</b>
              </code>
              . Be sure to take the following design considerations into account:
            </Paragraph>

            <ul>
              <li>
                Display the value of{' '}
                <code>
                  <b>user_code</b>
                </code>{' '}
                in an area wide enough to hold a sequence of eight <code>8</code> characters,{' '}
                <code>88888888</code>, without clipping.
              </li>
              <li>
                The value of{' '}
                <code>
                  <b>activate_link</b>
                </code>{' '}
                has no preset upper limit in terms of its length, although we try to keep it to as
                few characters as possible.
              </li>
              <li>
                Don't alter the values of{' '}
                <code>
                  <b>activate_link</b>
                </code>{' '}
                or{' '}
                <code>
                  <b>user_code</b>
                </code>{' '}
                in any way. The end user must visit that precise URL and enter that precise code.
              </li>
            </ul>

            <Paragraph size="2">
              When the end user arrives at the activation URL in their browser, they get a
              Vimeo-branded form for entering their user code. Assuming that they enter the code
              correctly, we prompt them to log into their Vimeo account, and then we ask them if
              it's okay for your app to access their private data. They can also choose exactly
              which scopes to permit or deny.
            </Paragraph>
          </FancyListItem>
          <FancyListItem header="Poll the authorization endpoint.">
            <Paragraph size="2">
              You can start polling the{' '}
              <code>
                <b>authorize_link</b>
              </code>{' '}
              URL for the end user's response as soon as you get the initial set of OAuth fields
              from the API (but don't poll more quickly than the value in{' '}
              <code>
                <b>interval</b>
              </code>
              ). Use a POST request for this:
            </Paragraph>

            <CodeBlock
              endpoint={{
                httpPrefix: false,
                method: 'post',
                resource: '{authorize_link}',
              }}
            />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-16">
                Table 16
              </Link>{' '}
              for the appropriate headers. To get the client identifier and client secret for the{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              header, go to{' '}
              <Link variant="minimal" href="/apps">
                My Apps
              </Link>
              , and open your app's information page.
            </Paragraph>

            <GuideTable
              tableNumber={16}
              description="Headers for POST to the authorization link in the device code grant"
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Authorization</b>
                  </code>,
                  <span>
                    <code>basic base64_encode(x:y),</code> where{' '}
                    <code>
                      <b>x</b>
                    </code>{' '}
                    is the client identifier and{' '}
                    <code>
                      <b>y</b>
                    </code>{' '}
                    is the client secret
                  </span>,
                ],
                [
                  <code>
                    <b>Content-Type</b>
                  </code>,
                  <code>application/json</code>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <Paragraph size="2">
              In the body of the request, set the{' '}
              <code>
                <b>user_code</b>
              </code>{' '}
              field to the user code and the{' '}
              <code>
                <b>device_code</b>
              </code>{' '}
              field to the device code. Both fields are required.
            </Paragraph>

            <CodeBlock language="json">
              {{
                user_code: '{user_code}',
                device_code: '{device_code}',
              }}
            </CodeBlock>

            <Paragraph size="2">
              If you run into errors, see{' '}
              <Link variant="minimal" href="#table-17">
                Table 17
              </Link>
              .
            </Paragraph>

            <GuideTable
              tableNumber={17}
              description="Common errors for POST to the authorization link in the device code grant"
              headers={['HTTP code', 'Error code', 'Description']}
              rows={[
                [
                  '400',
                  <code>invalid_token</code>,
                  "The code is invalid, the code has expired, or the code doesn't match the device.",
                ],
                [
                  '400',
                  <code>user_rejected_connection</code>,
                  "The end user has declined your app's request to access their personal data.",
                ],
              ]}
            />
          </FancyListItem>
          <FancyListItem header="Get the access token.">
            <Paragraph size="2">
              In the response from the API, assuming that the end user has entered the code, you get
              the access token and the end user's details (see{' '}
              <Link variant="minimal" href="#table-18">
                Table 18
              </Link>
              ).
            </Paragraph>

            <CodeBlock language="json">
              {{
                access_token: '{token}',
                token_type: 'bearer',
                scope: '{scope_list}',
                user: '{user_representation}',
              }}
            </CodeBlock>

            <GuideTable
              tableNumber={18}
              description="Second set of OAuth response fields in the device code grant"
              headers={['Field', 'Description']}
              rows={[
                [
                  <code>
                    <b>access_token</b>
                  </code>,
                  'The access token string.',
                ],
                [
                  <code>
                    <b>token_type</b>
                  </code>,
                  'The type of the access token. For the Vimeo API, the value of this field is always bearer.',
                ],
                [
                  <code>
                    <b>scope</b>
                  </code>,
                  'The space-separated list of scopes that your app has permission to access.',
                ],
                [
                  <code>
                    <b>user</b>
                  </code>,
                  <span>
                    The complete representation of the authenticated end user. For more information,
                    see the{' '}
                    <Link variant="minimal" href="/api/reference/users#get_user">
                      API Reference
                    </Link>
                    .
                  </span>,
                ],
              ]}
            />
          </FancyListItem>
        </FancyList>

        {canViewPasswordGrant && (
          <div>
            <Header size="3" id="using-the-password-grant">
              Using the password grant
            </Header>

            <Paragraph size="2">
              With the <em>password grant</em>, you send the end user's Vimeo login credentials to
              the API to obtain an authenticated access token. If alarm bells are going off in your
              head, they should be. You should use this workflow only with our official go-ahead
              (and when we've granted your app the necessary capability).
            </Paragraph>

            <Paragraph size="2">Here are the general steps:</Paragraph>

            <FancyList anchorPrefix="using-the-password-grant">
              <FancyListItem header="Make a POST request.">
                <Paragraph size="2">
                  Make a POST request to <code>/oauth/authorize/password</code>:
                </Paragraph>

                <CodeBlock
                  endpoint={{
                    method: 'post',
                    resource: '/oauth/authorize/password',
                  }}
                />

                <Paragraph size="2">
                  <Link variant="minimal" href="#table-19">
                    Table 19
                  </Link>{' '}
                  shows the appropriate headers. To get the client identification and client secret
                  for the <strong>Authorization</strong> header, go to{' '}
                  <Link variant="minimal" href="/apps">
                    My Apps
                  </Link>
                  , and click the link to your app's information page.
                </Paragraph>

                <GuideTable
                  tableNumber={19}
                  description={
                    <span>
                      Headers for POST to <code>/oauth/password</code> in the password grant
                    </span>
                  }
                  headers={['Field', 'Description']}
                  rows={[
                    [
                      <code>Authorization</code>,
                      <span>
                        <code>basic base64_encode(x:y)</code> where <strong>x</strong> is the client
                        ID and <strong>y</strong> is the client secret
                      </span>,
                    ],
                    [<code>Content-Type</code>, <code>application/json</code>],
                    [<code>Accept</code>, <code>application/vnd.vimeo.*+json;version=3.4</code>],
                  ]}
                />

                <Paragraph size="2">
                  As for the body of the request, send the <strong>grant_type</strong> field as{' '}
                  <code>device_code</code>, the <strong>username</strong> field as your end user's
                  Vimeo login name, and the <strong>password</strong> field as your end user's Vimeo
                  password. To request only certain scopes, include a space-separated list in the{' '}
                  <strong>scope</strong> field. Otherwise, the default scope is{' '}
                  <code>public private</code>.
                </Paragraph>

                <CodeBlock language="json">
                  {{
                    grant_type: 'password',
                    username: '{user_name}',
                    password: '{password}',
                    scope: '{scope_list}',
                  }}
                </CodeBlock>

                <Paragraph size="2">
                  See{' '}
                  <Link variant="minimal" href="#table-20">
                    Table 20
                  </Link>{' '}
                  for common errors.
                </Paragraph>

                <GuideTable
                  tableNumber={20}
                  description={
                    <span>
                      Common errors for POST to <code>/oauth/password</code> in the password grant
                    </span>
                  }
                  headers={['HTTP code', 'Error code', 'Description']}
                  rows={[
                    [
                      '400',
                      '2204',
                      <span>
                        You have an invalid value for the <code>grant_type</code> field, or we don't
                        recognize the <strong>username</strong> or <strong>password</strong> values.
                        One or both might be missing or incorrect.
                      </span>,
                    ],
                    [
                      '400',
                      '8001',
                      "We don't recognize your API app, or your app doesn't have the capability to use the password workflow.",
                    ],
                  ]}
                />
              </FancyListItem>
              <FancyListItem header="Get the access token.">
                <Paragraph size="2">
                  Grab the access token and user data from the API response (see{' '}
                  <Link variant="minimal" href="#table-21">
                    Table 21
                  </Link>
                  ).
                </Paragraph>

                <CodeBlock language="json">
                  {{
                    access_token: '{token}',
                    token_type: 'bearer',
                    scope: '{scope_list}',
                    user: '{user_representation}',
                  }}
                </CodeBlock>

                <GuideTable
                  tableNumber={21}
                  description="OAuth response fields in the password grant"
                  headers={['Field', 'Description']}
                  rows={[
                    [<code>access_token</code>, 'The access token string.'],
                    [
                      <code>token_type</code>,
                      <span>
                        The type of the access token, which for the Vimeo API is always{' '}
                        <code>bearer</code>.
                      </span>,
                    ],
                    [
                      <code>scope</code>,
                      'The space-separated list of scopes that your app has permission to access.',
                    ],
                    [
                      <code>user</code>,
                      <span>
                        The complete representation of the authenticated end user. For more
                        information, see the{' '}
                        <Link variant="minimal" href="/api/reference/users#get_user">
                          API Reference
                        </Link>
                        .
                      </span>,
                    ],
                  ]}
                />
              </FancyListItem>
            </FancyList>
          </div>
        )}

        <Header size="2" id="presenting-the-access-token">
          Presenting the access token to the API
        </Header>

        <Paragraph size="2">
          Whenever an API request requires an access token, include the{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          header with the request, and set the value of this header to{' '}
          <code>{`bearer {token}`}</code>, where the placeholder represents the access token string.
          Here's an example of what it looks like in cURL:
        </Paragraph>

        <CodeBlock>{`curl -H "Authorization: bearer {token}" https://api.vimeo.com`}</CodeBlock>

        <Paragraph size="2">
          If you forget the <code>bearer</code> keyword, or if the API doesn't recognize the access
          token string, or if you've omitted the{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          header entirely, we return HTTP status 401 Authorization Required along with error code
          8003.
        </Paragraph>

        <Paragraph size="2">
          If you're unable to provide your token through the{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          header, you might be able to send it by the{' '}
          <code>
            <b>access_token</b>
          </code>{' '}
          query parameter instead, like this (again in cURL):
        </Paragraph>

        <CodeBlock>{`curl https://api.vimeo.com/?access_token={token}`}</CodeBlock>

        <Paragraph size="2">
          But doing it this way could leak the token string to browsers, proxies, and multiple
          services. Because of this potential security vulnerability, the only access tokens that
          work as query parameters are those with the <code>public</code> scope.
        </Paragraph>

        <Header size="2" id="working-with-basic-authentication">
          Working with basic authentication
        </Header>

        <Paragraph size="2">
          In addition to OAuth 2.0, the Vimeo API also supports basic authentication. It's easier to
          implement than the workflows that we've already discussed, but it's also potentially less
          secure, so we don't recommend it for general use. And it restricts the kinds of
          information that you can fetch from the API: metadata is out, for example. However, in
          some limited cases, when acquiring an access token is awkward or unfeasible, and it's
          sufficient for you to view the API from the perspective of a logged-out user, basic
          authentication might provide a way forward.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> Basic authentication doesn't identify the end user of an app, but
          it does identify the app itself.
        </StyledNotice>

        <Paragraph size="2">To use basic authentication:</Paragraph>
        <ol>
          <li>
            Get the client identifier and client secret of the app that's interacting with the API.
            You can find this information on your app's information page, which you can access from{' '}
            <Link variant="minimal" href="/apps">
              My Apps
            </Link>
            .
          </li>
          <li>
            Create a string from the client identifier, a colon (<code>:</code>), and the client
            secret, like this:
            <CodeBlock>
              {`{client_id}`}:{`{client_secret}`}
            </CodeBlock>
          </li>
          <li>Base64-encode this string.</li>
          <li>
            Take the{' '}
            <code>
              <b>Authorization</b>
            </code>{' '}
            HTTP header, and set its value as <code>basic</code> plus the encoded credentials from
            Step 3, like this:
            <CodeBlock language="http">Authorization: basic {`{encoded_credentials}`}</CodeBlock>
          </li>
          <li>Include this header with your request.</li>
        </ol>

        <Header size="2" id="whats-next">
          What's next?
        </Header>

        <Paragraph size="2">
          We've covered the workflows for creating access tokens, and we've shown you how to use a
          token to access private data. But there are many other OAuth operations that you perform
          through the API, including verifying the validity of an access token and revoking an
          access token entirely. Complete details on all these procedures appear in the
          <Link variant="minimal" href="/api/reference/authentication-extras">
            {' '}
            Authentication Extras
          </Link>{' '}
          section of our exquisite API Reference.
        </Paragraph>

        <Paragraph size="2">
          And because there are more things in heaven and earth than are dreamt of in our
          philosophy, things that don't make sense, things that cause errors, things that lead you
          to believe that we intend for you to suffer, we invite you to{' '}
          <Link variant="minimal" href="http://www.vimeo.com/help/contact">
            contact us
          </Link>{' '}
          and not suffer in silence.
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
